import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      toast.error("Impossible d'envoyer l'email. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-af grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 bg-af-hero border-r border-af">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={52} />
          <span className="font-display font-extrabold text-2xl">AfriMeet</span>
        </Link>
        <div>
          <h2 className="font-display font-extrabold text-4xl leading-tight mb-4">
            Récupérez l&apos;accès à votre compte.
          </h2>
          <p className="text-af-secondary leading-relaxed max-w-md">
            Saisissez votre adresse email et nous vous enverrons un lien sécurisé pour réinitialiser votre mot de passe.
          </p>
        </div>
        <div className="text-xs text-af-muted">© 2026 AfriMeet</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <Link to="/" className="flex items-center gap-3">
              <Logo size={52} />
              <span className="font-display font-extrabold text-2xl">AfriMeet</span>
            </Link>
          </div>

          {sent ? (
            <div data-testid="forgot-sent-card">
              <div className="h-12 w-12 rounded-full bg-[#F5EFD8] flex items-center justify-center mb-5">
                <CheckCircle2 className="h-6 w-6 text-terracotta" />
              </div>
              <h1 className="font-display font-extrabold text-3xl mb-2">Vérifiez votre boîte mail</h1>
              <p className="text-af-secondary leading-relaxed">
                Si un compte existe avec l'adresse <strong className="text-af">{email}</strong>, vous
                recevrez sous peu un email contenant un lien pour réinitialiser votre mot de passe.
              </p>
              <p className="text-af-muted text-sm mt-4">
                Le lien expire dans 30 minutes. Pensez à vérifier vos spams.
              </p>
              <Link
                to="/auth/login"
                data-testid="forgot-back-login"
                className="inline-flex items-center gap-2 mt-8 text-terracotta font-semibold hover:underline"
              >
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display font-extrabold text-3xl mb-2">Mot de passe oublié ?</h1>
              <p className="text-af-secondary mb-8">
                Entrez votre email et nous vous enverrons un lien pour le réinitialiser.
              </p>
              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-af-muted pointer-events-none" />
                    <Input
                      id="email"
                      type="email"
                      required
                      data-testid="forgot-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9 border-af focus-visible:ring-terracotta"
                      placeholder="vous@entreprise.com"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="forgot-submit-btn"
                  className="w-full bg-terracotta hover:bg-[#9C7124] text-white h-11 rounded-md"
                >
                  {loading ? "Envoi…" : "Envoyer le lien de réinitialisation"}
                </Button>
              </form>
              <Link
                to="/auth/login"
                data-testid="forgot-back-login"
                className="inline-flex items-center gap-2 mt-6 text-terracotta font-semibold hover:underline text-sm"
              >
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
